import { useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@dhl-ui/components'

import { postData } from '~/api/admin-api'
import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { getErrorCode } from '~/common/util/axiosUtil'

import {
  CancelDialog,
  useCreateServicepointVendorValidationSchema,
  ServicepointVendorForm,
} from '~/modules/servicepointvendors/components'
import { useServicepointVendor } from '~/modules/servicepointvendors/hooks'
import { convertToServicepointVendorDto, type ServicepointVendor } from '~/modules/servicepointvendors/model'
import { BackButton } from '~/temp-ds-component/BackButton'

type Params = {
  id: string
}

export const EditServicepointVendorPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { id: userId } = useParams<Params>()
  const { showError, showSuccess } = useNotificationMessage()
  const validationSchema = useCreateServicepointVendorValidationSchema()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const { servicepoint, isLoading } = useServicepointVendor(userId)

  const onSubmit = async (updatedServicepoint: ServicepointVendor) => {
    const updatedServicepointDto = convertToServicepointVendorDto(updatedServicepoint)
    try {
      await postData(`vendors`, 'PUT', updatedServicepointDto)
      showSuccess(t('admin.servicePointVendorsPage.editServicePointVendorSuccess'))
      navigate(links.servicePointVendorsLink())
    } catch (error) {
      const errorCode = getErrorCode(error)
      switch (errorCode) {
        case 'EXTERNAL_ID_NOT_UNIQUE':
          showError(t('admin.servicePointVendorsPage.servicePointVendorNotUniqueError'))
          break
        case 'EXTERNAL_ID_INVALID':
          showError(t('admin.servicePointVendorsPage.servicePointVendorInvalidError'))
          break
        default:
          showError(t('admin.servicePointVendorsPage.editServicePointVendorError'))
      }
    }
  }

  const onBack = (dirty: boolean) =>
    dirty && userId ? setCancelDialogOpen(true) : navigate(links.servicePointVendorsLink())
  if (isLoading) return null
  return (
    <>
      {servicepoint && (
        <Formik
          initialValues={servicepoint}
          validateOnMount
          validateOnChange
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ dirty }) => (
            <Box p={2}>
              <Box display="flex" justifyContent="space-between" pb={2}>
                <BackButton onClick={() => onBack(dirty)}>
                  {t('admin.servicePointVendorsPage.servicePointVendorsForm.backButtonTitle')}
                </BackButton>
              </Box>
              <ServicepointVendorForm />
            </Box>
          )}
        </Formik>
      )}
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  )
}
